import styled from '@emotion/styled';
import { Button } from 'primereact/button';
import React, { type ReactNode, useEffect, useRef } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';

/** Prevents multiple redirects on remount (e.g. React Strict Mode). */
let signinRedirectTriggered = false;

interface ProtectedAppProps {
  children: ReactNode;
}

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-family: "Teko", sans-serif;
  text-align: center;
  margin-bottom: 2rem;
`;

const PageButton = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
`;

export const ProtectedApp: React.FC<ProtectedAppProps> = (props) => {
  const { children } = props;
  const auth = useAuth();
  const hasTriedSigninRef = useRef(false);

  useEffect(() => {
    if (hasAuthParams() || auth.isAuthenticated || auth.activeNavigator || auth.isLoading) {
      return;
    }
    if (signinRedirectTriggered || hasTriedSigninRef.current) {
      return;
    }
    hasTriedSigninRef.current = true;
    signinRedirectTriggered = true;
    auth.signinRedirect();
  }, [auth.isAuthenticated, auth.activeNavigator, auth.isLoading, auth]);

  switch (auth.activeNavigator) {
    case 'signinRedirect':
      return <p className={'fixed bottom-4 right-4 text-xs text-[#bfc5d0]'}>Redirecting to login...</p>;
    case 'signinSilent':
      return <p className={'fixed bottom-4 right-4 text-xs text-[#bfc5d0]'}>Signing you in...</p>;
    case 'signoutRedirect':
      return <p className={'fixed bottom-4 right-4 text-xs text-[#bfc5d0]'}>Signing you out...</p>;
  }

  return (
    <>
      {auth.error ? (
        <>
          <PageTitle>Oops. There has been problem with authentication.</PageTitle>
          <PageButton>
            <Button className={'btn-primary'} onClick={() => auth.signinRedirect()}>
              Login
            </Button>
          </PageButton>
        </>
      ) : auth.isLoading ? (
        <>
          <p className={'fixed bottom-4 right-4 text-xs text-[#bfc5d0]'}>Authenticating...</p>
        </>
      ) : auth.isAuthenticated ? (
        children
      ) : (
        <>
          <PageTitle>Welcome back. Please login to your account.</PageTitle>
          <PageButton>
            <Button className={'btn-primary'} onClick={() => auth.signinRedirect()}>
              Login
            </Button>
          </PageButton>
        </>
      )}
    </>
  );
};
